import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, client } from '@xinfer/api-client';
import type { Account } from '@xinfer/api-client';
import {
  AppShell,
  Badge,
  ErrorBoundary,
  ErrorState,
  LoadingState,
  UserMenu,
  type NavItem,
} from '@xinfer/ui';
import {
  BookOpen,
  CircleDollarSign,
  Gauge,
  KeyRound,
  LayoutDashboard,
  SlidersHorizontal,
} from 'lucide-react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { SignIn } from './pages/SignIn';
import { Overview } from './pages/Overview';
import { Keys } from './pages/Keys';
import { Usage } from './pages/Usage';
import { Credits } from './pages/Credits';
import { Limits } from './pages/Limits';
import { Quickstart } from './pages/Quickstart';
import { NotFound } from './pages/NotFound';

const NAV: NavItem[] = [
  { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/keys', label: 'API keys', icon: KeyRound },
  { to: '/usage', label: 'Usage', icon: Gauge },
  { to: '/credits', label: 'Plan & credits', icon: CircleDollarSign },
  { to: '/limits', label: 'Limits', icon: SlidersHorizontal },
  { to: '/quickstart', label: 'Quickstart', icon: BookOpen },
];

export function App() {
  const queryClient = useQueryClient();

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => client.me(),
    // 401 is not a failure to retry: it means "show the login page".
    retry: (count, error) => !(error instanceof ApiError && error.isUnauthenticated) && count < 2,
  });

  const logout = useMutation({
    mutationFn: () => client.logout(),
    onSuccess: () => queryClient.clear(),
  });

  if (me.isLoading) {
    return (
      <div className="p-8">
        <LoadingState title="Loading your account…" />
      </div>
    );
  }

  if (me.error instanceof ApiError && me.error.isUnauthenticated) {
    return <SignIn onSignedIn={() => queryClient.invalidateQueries({ queryKey: ['me'] })} />;
  }

  if (me.isError || !me.data) {
    return (
      <div className="p-8">
        <ErrorState
          title="The control plane is unreachable"
          description="Keys and usage cannot be read right now. Inference is served by the gate and is unaffected by this."
        />
      </div>
    );
  }

  // Every account has exactly one member today, so there is no switcher and no
  // members page. `default_account_id` is what the SPA follows; when a second
  // account appears, this is where a switcher goes and nothing else changes.
  const account = me.data.accounts.find((a) => a.id === me.data?.default_account_id);
  if (!account) {
    return (
      <div className="p-8">
        <ErrorState
          title="This login has no account"
          description="Sign-in succeeded but no account is attached to it, which is a control-plane problem rather than something that can be fixed from here."
        />
      </div>
    );
  }

  const user = me.data.user;

  return (
    <AppShell
      brand="xinfer"
      nav={NAV}
      sidebarFooter={
        <UserMenu
          name={user.name ?? user.email ?? user.user_id}
          secondary={user.email}
          onSignOut={() => logout.mutate()}
          signingOut={logout.isPending}
        />
      }
      topBar={<AccountSummary account={account} />}
    >
      {/* Scoped to the content column, so a page that throws leaves the
          navigation standing and the user can get somewhere else. */}
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<Overview account={account} />} />
          <Route path="/keys" element={<Keys account={account} />} />
          <Route path="/usage" element={<Usage account={account} />} />
          <Route path="/credits" element={<Credits account={account} />} />
          <Route path="/limits" element={<Limits account={account} />} />
          <Route path="/quickstart" element={<Quickstart account={account} />} />
          {/* Nothing was addressable before the router, so there are no old
              links to preserve — only the obvious guess at this one. */}
          <Route path="/overview" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ErrorBoundary>
    </AppShell>
  );
}

/**
 * The account's plan and status, in the top bar on every page.
 *
 * Status especially: a suspended account is refused with `403 account_inactive`
 * on every inference call, and the portal is where somebody works out why.
 * Showing it only on Overview means seeing it only if you happen to land there.
 */
function AccountSummary({ account }: { account: Account }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="truncate text-sm font-medium">{account.name}</span>
      <Badge variant="outline" className="shrink-0">
        {account.plan}
      </Badge>
      {account.status !== 'active' && (
        <Badge variant="warning" className="shrink-0">
          {account.status}
        </Badge>
      )}
    </div>
  );
}
